import { normalize, schema } from 'normalizr'
import {
  IPaymentSourceResponseDTO,
  IRiderObject,
  IRiderSearchState,
  IRiderState,
  IRiderLocationObject,
  IAppliedPromotion,
  ITripRequestObject
} from '../../../types'
import UIMessages from '../../../utils/ui-messages'
import { IRiderAction } from '../../actions/riders'
import { IRiderLocationAction } from '../../actions/rider-locations'
import * as constants from '../../constants/'
import { riderService } from 'src/services/Rider'
import { ILogEntryResponseDTO } from 'src/types/log-entry'

const PAGE_SIZE_DEFAULT = 10

const initialState: IRiderState = {
  riderSavedId: '-1',
  rider: {
    id: '',
    name: '',
    firstName: '',
    lastName: '',
    memberships: [],
    membershipServiceIds: [],
    email: '',
    birthDate: undefined,
    phone: '',
    countryCode: '',
    isActive: true,
    isArchived: false,
    additionalInfo: '',
    notificationPreference: '',
    username: '',
    saved: false,
    locations: [],
    locationActivity: [],
    mobilityOptions: [],
    allowFreeAttendant: false,
    assistances: [],
    walletAutocharge: {
      paymentSource: '',
      lowerLimit: undefined,
      rechargeTo: undefined,
      isEnabled: false,
      userId: ''
    },
    wallet: {
      wallet: {
        id: '',
        stripeCustomerId: '',
        balance: 0,
        bonusBalance: 0,
        currency: '',
        userId: '',
        orgId: ''
      },
      sources: []
    },
    walletTransactions: [],
    walletTransactionHistory: [],
    ownerOrganization: UIMessages.rider.defaultValues.organization.value
  },
  isFetching: false,
  items: {},
  lastUpdated: Date.now(),
  metadata: {
    fetching: false,
    fetchingError: '',
    fetchingSuccess: '',
    processing: false,
    processingSuccess: '',
    processingError: '',
    pageCount: 0,
    pageSize: 0,
    total: 0
  },
  organizations: {},
  organizationsFetchingMetaData: {
    fetching: false,
    fetchingError: '',
    fetchingSuccess: '',
    processing: false,
    processingError: '',
    processingSuccess: ''
  },
  deactivatedRiderId: ''
}

const initialSearchState: IRiderSearchState = {
  isFetching: false,
  items: [],
  query: '',
  metadata: {
    pageCount: 0,
    pageSize: 0,
    total: 0
  }
}

export function riders(state: IRiderState, action: IRiderAction | IRiderLocationAction): IRiderState {
  if (typeof state === 'undefined') {
    return initialState
  }
  const metadata = {
    fetching: false,
    fetchingError: '',
    fetchingSuccess: '',
    processing: false,
    processingError: '',
    processingSuccess: '',
    pageCount: 0,
    pageSize: 0,
    total: 0
  }
  switch (action.type) {
    case constants.RIDERS_WALLET_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_WALLET_SUCCESS: {
      const { riderId, wallet } = action.payload
      const rider = Object.assign({}, state.items[riderId])
      const items = Object.assign({}, state.items)
      rider.wallet = wallet
      items[riderId] = rider
      metadata.fetching = false
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDERS_WALLET_FAILURE: {
      metadata.fetchingError = action.payload.error
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_FETCH_WALLET_AUTOCHARGE_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_UPDATE_WALLET_AUTOCHARGE_SUCCESS:
    case constants.RIDERS_FETCH_WALLET_AUTOCHARGE_SUCCESS: {
      const { riderId, autochargeConfig } = action.payload
      const rider = Object.assign({}, state.items[riderId])
      const items = Object.assign({}, state.items)
      if (autochargeConfig) {
        rider.walletAutocharge = autochargeConfig
      } else {
        rider.walletAutocharge = initialState.rider.walletAutocharge
      }
      items[riderId] = rider
      metadata.fetching = false
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDERS_FETCH_WALLET_AUTOCHARGE_FAILURE: {
      metadata.fetchingError = action.payload.error
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_WALLET_TRANSACTIONS_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_WALLET_TRANSACTIONS_SUCCESS: {
      const { riderId, transactions } = action.payload
      const rider = Object.assign({}, state.items[riderId])
      const items = Object.assign({}, state.items)
      rider.walletTransactions = transactions
      items[riderId] = rider
      metadata.fetching = false
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDERS_WALLET_TRANSACTIONS_FAILURE: {
      metadata.fetchingError = action.payload.error
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_WALLET_TRANSACTION_HISTORY_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_WALLET_TRANSACTION_HISTORY_SUCCESS: {
      const { riderId, transactions } = action.payload
      const rider = Object.assign({}, state.items[riderId])
      const items = Object.assign({}, state.items)
      rider.walletTransactionHistory = transactions
      items[riderId] = rider
      metadata.fetching = false
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDERS_WALLET_TRANSACTION_HISTORY_FAILURE: {
      metadata.fetchingError = action.payload.error
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_ADD_PAYMENT_SOURCE_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_ADD_PAYMENT_SOURCE_SUCCESS: {
      const { riderId, wallet } = action.payload
      console.log('RIDERS_ADD_PAYMENT_SOURCE_SUCCESS1', action)
      const items = Object.assign({}, state.items)
      const rider = state.items[riderId]
      rider.wallet = wallet
      items[riderId] = rider
      metadata.fetchingSuccess = UIMessages.rider.assistance.successfullyFetched
      console.log('RIDERS_ADD_PAYMENT_SOURCE_SUCCESS2', items)
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }

    case constants.RIDERS_ADD_PAYMENT_SOURCE_FAILURE: {
      metadata.fetchingError = action.payload
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_UPDATE_DEFAULT_PAYMENT_SOURCE_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_UPDATE_DEFAULT_PAYMENT_SOURCE_SUCCESS: {
      const { riderId, wallet } = action.payload
      const items = Object.assign({}, state.items)
      const rider = state.items[riderId]
      rider.wallet = wallet
      items[riderId] = rider
      metadata.fetchingSuccess = UIMessages.rider.assistance.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }

    case constants.RIDERS_UPDATE_DEFAULT_PAYMENT_SOURCE_FAILURE: {
      metadata.fetchingError = action.payload
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_REMOVE_PAYMENT_SOURCE_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_REMOVE_PAYMENT_SOURCE_SUCCESS: {
      const { paymentSourceId, riderId } = action.payload
      const rider = Object.assign({}, state.items[riderId])
      if (rider && rider.wallet) {
        const sourceIdx = rider.wallet.sources.findIndex(
          (item: IPaymentSourceResponseDTO) => item.id === paymentSourceId
        )
        if (sourceIdx >= 0) {
          const result = rider.wallet.sources.splice(sourceIdx, 1)
        }

        //If there is only one card left, it is now the default
        if (rider.wallet.sources.length === 1) {
          rider.wallet.sources[0].isDefault = true
        }
      }
      const items = Object.assign({}, state.items)
      metadata.processing = false
      metadata.processingSuccess = UIMessages.rider.payment.successfullyRemoved
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDERS_REMOVE_PAYMENT_SOURCE_FAILURE: {
      metadata.fetchingError = action.payload
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_ASSISTANCE_LIST_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_ASSISTANCE_LIST_SUCCESS: {
      const items = Object.assign({}, state.items)
      const { riderId: id, assistanceList: assistances = [] } = action.payload
      items[id] = Object.assign({}, items[id], { id, assistances })
      metadata.fetchingSuccess = UIMessages.rider.assistance.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDER_ASSISTANCE_LIST_FAILURE: {
      metadata.fetchingError = UIMessages.rider.assistance.errorOnFetching
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_LOCATION_LIST_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_LOCATION_LIST_SUCCESS: {
      const items = Object.assign({}, state.items)
      const { riderId: id, locationList: locations = [] } = action.payload
      items[id] = Object.assign({}, items[id], { id, locations: Object.keys(locations) })
      metadata.fetchingSuccess = UIMessages.rider.location.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }

    case constants.RIDER_LOCATION_ACTIVITY_LIST_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_LOCATION_ACTIVITY_LIST_SUCCESS: {
      console.log({ payload: action.payload })

      const items = Object.assign({}, state.items)

      const { riderId: id, locationList: locationActivity = [] } = action.payload

      items[id] = Object.assign({}, items[id], { id, locationActivity })

      metadata.fetchingSuccess = UIMessages.rider.location.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }

    case constants.RIDER_MEMBERSHIP_LIST_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_MEMBERSHIP_LIST_SUCCESS: {
      const items = Object.assign({}, state.items)
      const { riderId: id, membershipList: memberships = [] } = action.payload
      const membershipServiceIds = memberships.map(m => m.service && m.service.id).filter(id => id)
      items[id] = Object.assign({}, items[id], {
        id,
        memberships: Object.keys(memberships),
        membershipServiceIds
      })
      metadata.fetchingSuccess = UIMessages.rider.membership.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDER_MEMBERSHIP_LIST_FAILURE: {
      metadata.fetchingError = UIMessages.rider.membership.errorOnFetching
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_MEMBERSHIP_UPDATE_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_MEMBERSHIP_UPDATE_SUCCESS: {
      metadata.fetching = false
      const items = { ...state.items }
      delete items[action.payload]
      return Object.assign({}, state, { items })
    }

    case constants.RIDER_MOBILITY_OPTION_LIST_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_MOBILITY_OPTION_LIST_SUCCESS: {
      const items = Object.assign({}, state.items)
      const { riderId: id, mobilityOptionList: mobilityOptions = [] } = action.payload
      items[id] = Object.assign({}, items[id], { id, mobilityOptions })
      metadata.fetchingSuccess = UIMessages.rider.mobility.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDER_MOBILITY_OPTION_LIST_FAILURE: {
      metadata.fetchingError = UIMessages.rider.mobility.errorOnFetching
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_PERSONAL_INFO_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_PERSONAL_INFO_SUCCESS: {
      const items = Object.assign({}, state.items)
      const rider = action.payload
      items[rider.id] = Object.assign({}, items[rider.id], rider)
      metadata.fetchingSuccess = UIMessages.rider.personalInfo.successfullyFetched
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.RIDER_PERSONAL_INFO_FAILURE: {
      metadata.fetchingError = UIMessages.rider.personalInfo.errorOnFetching
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_LIST_REQUEST:
      metadata.fetching = true
      return Object.assign({}, state, { metadata })

    case constants.RIDER_LIST_SUCCESS: {
      const { items, total } = action.payload
      metadata.fetchingSuccess = UIMessages.rider.list.successfullyFetched
      metadata.total = total
      metadata.pageSize = PAGE_SIZE_DEFAULT
      metadata.pageCount = Math.ceil(total / PAGE_SIZE_DEFAULT) //todo: pass page size when implemented on table
      const riders = {}
      items.forEach(rider => {
        riders[rider.id] = rider
      })
      return Object.assign({}, state, {
        metadata,
        items: riders,
        lastUpdated: Date.now()
      })
    }

    case constants.RIDER_LIST_FAILURE: {
      metadata.fetchingError = UIMessages.rider.list.errorOnFetching
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_SEARCH_SUCCESS: {
      const { riders, total } = action.payload
      metadata.total = total
      metadata.pageSize = PAGE_SIZE_DEFAULT
      metadata.pageCount = Math.ceil(total / PAGE_SIZE_DEFAULT)
      return Object.assign({}, state, {
        isFetching: false,
        metadata,
        items: riders
      })
    }
    case constants.RIDERS_ACTIVATE_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.RIDERS_ACTIVATE_SUCCESS: {
      const rider = Object.assign({}, action.payload)
      const items = Object.assign({}, state.items)
      items[rider.id] = rider
      metadata.processingSuccess = UIMessages.rider.status.successfullyActivated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_ACTIVATE_FAILURE: {
      const rider = Object.assign({}, action.payload.rider)
      const items = Object.assign({}, state.items)
      items[rider.id] = rider
      metadata.processingError = UIMessages.rider.status.errorOnActivate
      return Object.assign({}, state, { items, metadata })
    }

    case constants.RIDERS_DEACTIVATE_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.RIDERS_DEACTIVATE_SUCCESS: {
      const rider = Object.assign({}, action.payload)
      rider.isActive = false
      const items = Object.assign({}, state.items)
      items[rider.id] = rider
      state.deactivatedRiderId = rider.id
      metadata.processingSuccess = UIMessages.rider.status.successfullyDeactivated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_DEACTIVATE_FAILURE: {
      const rider = Object.assign({}, action.payload.rider)
      const items = Object.assign({}, state.items)
      items[rider.id] = rider
      metadata.processingError = UIMessages.rider.status.errorOnDeactivate
      return Object.assign({}, state, { items, metadata })
    }

    case constants.RIDERS_ARCHIVE_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.RIDERS_ARCHIVE_SUCCESS: {
      const items = { ...state.items }
      delete items[action.payload.id]
      metadata.processingSuccess = UIMessages.rider.status.successfullyArchived
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_ARCHIVE_FAILURE: {
      const rider = Object.assign({}, action.payload.rider)
      const items = Object.assign({}, state.items)
      items[rider.id] = rider
      metadata.processingError = UIMessages.rider.status.errorOnArchive
      return Object.assign({}, state, { items, metadata })
    }

    case constants.RIDERS_RESTORE_ARCHIVE_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })

    case constants.RIDERS_RESTORE_ARCHIVE_SUCCESS: {
      const items = { ...state.items }
      delete items[action.payload.id]
      metadata.processingSuccess = UIMessages.rider.status.successfullyRestored
      return Object.assign({}, state, { items, metadata })
    }

    case constants.RIDERS_CREATE_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.RIDERS_CREATE_SUCCESS: {
      const rider = Object.assign({}, action.payload)
      const items = Object.assign({}, state.items)
      items[rider.id] = Object.assign({}, rider)
      metadata.processingSuccess = UIMessages.rider.personalInfo.successfullyCreated
      return Object.assign({}, state, {
        items,
        metadata,
        riderSavedId: rider.id
      })
    }
    case constants.RIDERS_CREATE_FAILURE: {
      metadata.processingError = UIMessages.rider.personalInfo.errorOnCreate + ': ' + action.payload
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_CREATE_FINISH: {
      return Object.assign({}, state, { riderSavedId: '-1' })
    }

    case constants.RIDERS_UPDATE_PERSONAL_INFO_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.RIDERS_UPDATE_PERSONAL_INFO_SUCCESS: {
      const rider = Object.assign({}, action.payload.rider)
      const items = Object.assign({}, state.items)
      items[rider.id] = Object.assign({}, rider)
      metadata.processingSuccess = UIMessages.rider.personalInfo.successfullyUpdated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_UPDATE_PERSONAL_INFO_FAILURE: {
      metadata.processingError = UIMessages.rider.personalInfo.errorOnUpdate + ': ' + action.payload
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_CREATE_CLEAR: {
      return Object.assign({}, state, {
        riderSavedId: '-1',
        rider: {
          id: '1',
          firstName: '',
          lastName: '',
          memberships: [],
          email: '',
          birthDate: '',
          phone: '',
          countryCode: '',
          active: true,
          archived: false,
          additionalInfo: '',
          notificationPreference: '',
          username: '',
          saved: false,
          locations: [],
          assistances: []
        }
      })
    }

    case constants.RIDERS_UPDATE_MOBILITIES_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_UPDATE_MOBILITIES_SUCCESS: {
      const { riderId, mobilityOptionList, allowAttendant } = action.payload
      const items = Object.assign({}, state.items)
      const modifiedRider = Object.assign({}, items[riderId])
      modifiedRider.mobilityOptions = mobilityOptionList
      modifiedRider.allowFreeAttendant = allowAttendant
      items[riderId] = modifiedRider
      metadata.processingSuccess = UIMessages.rider.mobility.successfullyUpdated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_UPDATE_MOBILITIES_FAILURE: {
      metadata.processingError = UIMessages.rider.mobility.errorOnUpdate
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_UPDATE_ASSISTANCES_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_UPDATE_ASSISTANCES_SUCCESS: {
      const { rider, assistances } = action.payload
      const items = Object.assign({}, state.items)
      const modifiedRider = Object.assign({}, items[rider.id])
      modifiedRider.assistances = assistances.slice(0)
      items[rider.id] = modifiedRider
      metadata.processingSuccess = UIMessages.rider.assistance.successfullyUpdated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_UPDATE_ASSISTANCES_FAILURE: {
      metadata.processingError = UIMessages.rider.assistance.errorOnUpdate
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_CREATE_LOCATION_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_CREATE_LOCATION_SUCCESS: {
      metadata.processingSuccess = UIMessages.rider.location.successfullyCreated
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_UPDATE_LOCATIONS_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_UPDATE_LOCATIONS_SUCCESS: {
      metadata.processingSuccess = UIMessages.rider.location.successfullyUpdated
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_REMOVE_LOCATION_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_REMOVE_LOCATION_SUCCESS: {
      metadata.processingSuccess = UIMessages.rider.location.successfullyDeleted
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_ADD_FUNDS_TO_WALLET_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_ADD_FUNDS_TO_WALLET_SUCCESS: {
      const { riderId, wallet } = action.payload
      const items = Object.assign({}, state.items)
      const rider = items[riderId]

      delete rider.wallet.wallet
      rider.wallet.wallet = wallet
      metadata.processingSuccess = UIMessages.rider.wallet.successfullyAddedFunds
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_ADD_FUNDS_TO_WALLET_FAILURE: {
      metadata.processingError = UIMessages.rider.wallet.errorOnAddFunds
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_ADD_BONUS_TO_WALLET_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_ADD_BONUS_TO_WALLET_SUCCESS: {
      const { riderId, wallet } = action.payload
      const items = Object.assign({}, state.items)
      const rider = items[riderId]

      delete rider.wallet.wallet
      rider.wallet.wallet = wallet
      metadata.processingSuccess = UIMessages.rider.wallet.successfullyAddedFunds
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_ADD_BONUS_TO_WALLET_FAILURE: {
      metadata.processingError = UIMessages.rider.wallet.errorOnAddFunds
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_DETAILS_SUSPEND_MEMBERSHIP_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_DETAILS_SUSPEND_MEMBERSHIP_SUCCESS: {
      metadata.processingSuccess = UIMessages.rider.membership.successfullySuspended
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_DETAILS_SUSPEND_MEMBERSHIP_FAILURE: {
      metadata.processingError = UIMessages.rider.membership.errorOnSuspend
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDERS_UPDATE_MEMBERSHIPS_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_UPDATE_MEMBERSHIPS_SUCCESS: {
      const { rider, memberships } = action.payload
      const items = Object.assign({}, state.items)
      //TODO fixme
      rider.memberships = memberships.map(m => m.id || '')
      items[rider.id] = rider
      metadata.processingSuccess = UIMessages.rider.membership.successfullyUpdated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.RIDERS_UPDATE_MEMBERSHIPS_FAILURE: {
      metadata.processingError = UIMessages.rider.membership.errorOnUpdate
      return Object.assign({}, state, { metadata })
    }
    case constants.APPLY_PROMOTION: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.APPLY_PROMOTION_SUCCESS: {
      const appliedPromotion: IAppliedPromotion = action.payload
      const appliedPromotionsSchema = [new schema.Entity('appliedPromotions')]
      const rider = Object.assign({}, state.items[action.payload.userId])
      const items = Object.assign({}, state.items)
      const { entities } = normalize([appliedPromotion], appliedPromotionsSchema)
      rider.appliedPromotions = {
        ...rider.appliedPromotions,
        ...entities.appliedPromotions
      }
      items[action.payload.userId] = rider
      return Object.assign({}, state, { items, metadata })
    }
    case constants.APPLY_PROMOTION_FAIL: {
      metadata.processingError = action.payload
      return Object.assign({}, state, { metadata })
    }
    case constants.GET_APPLIED_PROMOTIONS: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.GET_APPLIED_PROMOTIONS_SUCCESS: {
      const appliedPromotions: IAppliedPromotion[] = action.payload.appliedPromotions
      const appliedPromotionsSchema = [new schema.Entity('appliedPromotions')]
      const rider = Object.assign({}, state.items[action.payload.userId])
      const items = Object.assign({}, state.items)
      const { entities } = normalize(appliedPromotions, appliedPromotionsSchema)
      rider.appliedPromotions = {
        ...rider.appliedPromotions,
        ...entities.appliedPromotions
      }
      items[action.payload.userId] = rider
      return Object.assign({}, state, { items, metadata })
    }
    case constants.DELETE_APPLIED_PROMOTIONS_FAILURE:
    case constants.GET_APPLIED_PROMOTIONS_FAILURE: {
      metadata.processingError = action.payload
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDER_RECURRING_TRIPS_REQUEST: {
      return Object.assign({}, state, {
        isFetching: true
      })
    }

    case constants.RIDER_RECURRING_TRIPS_SUCCESS: {
      const result: { items: ITripRequestObject[]; count: number } = action.payload
      return Object.assign({}, state, {
        rider: {
          riderRecurringTrips: {
            items: result.items,
            count: result.count
          },
          isFetching: false
        }
      })
    }
    case constants.RIDER_CHANGE_LOG_SUCCESS: {
      const result: { items: ILogEntryResponseDTO[]; count: number } = action.payload
      return Object.assign({}, state, {
        rider: {
          riderChangeLog: {
            items: result.items,
            count: result.count
          },
          isFetching: false
        }
      })
    }
    case constants.DESTROY_RECURRING_TRIP_TASK_SUCCESS: {
      return Object.assign({}, state, {
        rider: {
          destroyedRecurringTaskId: action.payload,
          isFetching: false
        }
      })
    }
    case constants.GET_REFUNDED_AMOUNT_SUCCESS: {
      return Object.assign({}, state, {
        rider: {
          ...state.rider,
          refundedAmount: action.payload
        }
      })
    }

    case constants.REFUND_PAYMENT_SUCCESS: {
      metadata.processingSuccess = UIMessages.rider.wallet.successfullyRefunded
      return Object.assign({}, state, { metadata })
    }

    case constants.ASSIGN_REFUND_DETAILS: {
      return Object.assign({}, state, {
        rider: {
          ...state.rider,
          refundDetails: action.payload
        }
      })
    }

    case constants.RIDERS_RECHARGE_WALLET_REQUEST: {
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    }
    case constants.RIDERS_RECHARGE_WALLET_SUCCESS: {
      metadata.processingSuccess = UIMessages.rider.wallet.successfullyRechargedWallet
      return Object.assign({}, state, { metadata })
    }

    case constants.RIDER_CHANGE_LOG_FAILURE:
    case constants.RIDERS_UPDATE_WALLET_AUTOCHARGE_FAILURE:
    case constants.GET_REFUNDED_AMOUNT_FAILURE:
    case constants.DESTROY_RECURRING_TRIP_TASK_FAIL:
    case constants.REFUND_PAYMENT_FAILURE:
    case constants.RIDERS_RECHARGE_WALLET_FAILURE:
    case constants.RIDER_RECURRING_TRIPS_FAILURE: {
      metadata.processingError = action.payload
      return Object.assign({}, state, { metadata })
    }
  }

  return state
}

export function riderSearch(state: IRiderSearchState, action: IRiderAction): any {
  //todo:for pagination
  const metadata = {
    pageCount: 0,
    pageSize: 0,
    total: 0
  }
  if (typeof state === 'undefined') {
    return initialSearchState
  }
  switch (action.type) {
    case constants.RIDERS_SEARCH_REQUEST:
      return Object.assign({}, state, {
        isFetching: true,
        query: action.payload.query
      })

    case constants.TRIP_RIDER_SEARCH_REQUEST:
      return Object.assign({}, state, {
        isFetching: true,
        query: action.payload.search
      })
    case constants.TRIP_RIDER_SEARCH_SUCCESS:
      const { items, total } = action.payload
      metadata.total = total
      metadata.pageSize = PAGE_SIZE_DEFAULT
      metadata.pageCount = Math.ceil(total / PAGE_SIZE_DEFAULT)
      return Object.assign({}, state, {
        isFetching: false,
        metadata,
        items
      })
    case constants.TRIP_RIDER_SEARCH_FAIL:
      return Object.assign({}, state, {
        isFetching: false
      })
    case constants.CLEAR_RIDER_SEARCH:
      return Object.assign({}, state, {
        items: [],
        query: ''
      })
  }
  return state
}

export const getRiderById = (riders: IRiderState, riderId: string): IRiderObject | undefined => {
  return riders.items[riderId]
}

export const getRidersList = (state: IRiderState) => {
  if (!state.items) {
    return []
  }
  return Object.values(state.items)
}



// WEBPACK FOOTER //
// ./src/state/reducers/riders/index.ts